import * as React from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "../../lib/utils";

const AccordionContext = React.createContext(null);

function Accordion({ type = "single", value, defaultValue, onValueChange, collapsible = false, className, children, ...props }) {
  const [internalValue, setInternalValue] = React.useState(defaultValue ?? (type === "multiple" ? [] : ""));
  const currentValue = value ?? internalValue;

  const setValue = React.useCallback(
    (itemValue) => {
      let nextValue;
      if (type === "multiple") {
        const currentArray = Array.isArray(currentValue) ? currentValue : [];
        nextValue = currentArray.includes(itemValue)
          ? currentArray.filter((v) => v !== itemValue)
          : [...currentArray, itemValue];
      } else {
        nextValue = currentValue === itemValue && collapsible ? "" : itemValue;
      }

      if (value === undefined) setInternalValue(nextValue);
      onValueChange?.(nextValue);
    },
    [collapsible, currentValue, onValueChange, type, value]
  );

  return (
    <AccordionContext.Provider value={{ type, value: currentValue, setValue }}>
      <div className={cn("tw-w-full", className)} {...props}>
        {children}
      </div>
    </AccordionContext.Provider>
  );
}

const AccordionItemContext = React.createContext(null);

function AccordionItem({ value, className, children, ...props }) {
  return (
    <AccordionItemContext.Provider value={value}>
      <div className={cn("tw-border-b tw-border-white/[0.08]", className)} {...props}>
        {children}
      </div>
    </AccordionItemContext.Provider>
  );
}

const AccordionTrigger = React.forwardRef(({ className, children, style, ...props }, ref) => {
  const accordion = React.useContext(AccordionContext);
  const itemValue = React.useContext(AccordionItemContext);
  const open = accordion?.type === "multiple"
    ? Array.isArray(accordion.value) && accordion.value.includes(itemValue)
    : accordion?.value === itemValue;

  return (
    <button
      ref={ref}
      type="button"
      aria-expanded={open}
      onClick={() => accordion?.setValue(itemValue)}
      className={cn(
        "tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-3 tw-py-4 tw-text-left tw-text-sm tw-font-medium tw-text-white tw-transition-all hover:tw-text-primary-light [&[data-state=open]>svg]:tw-rotate-180",
        className
      )}
      data-state={open ? "open" : "closed"}
      style={{
        display: "flex",
        width: "100%",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        textAlign: "left",
        fontSize: 14,
        fontWeight: 600,
        transition: "color 0.15s",
        ...style,
      }}
      {...props}
    >
      {children}
      <ChevronDown
        size={16}
        className="tw-size-4 tw-shrink-0 tw-text-muted tw-transition-transform tw-duration-200"
        style={{
          flexShrink: 0,
          color: "#b8c1c8",
          transform: open ? "rotate(180deg)" : "rotate(0deg)",
          transition: "transform 0.2s",
        }}
      />
    </button>
  );
});
AccordionTrigger.displayName = "AccordionTrigger";

const AccordionContent = React.forwardRef(({ className, children, ...props }, ref) => {
  const accordion = React.useContext(AccordionContext);
  const itemValue = React.useContext(AccordionItemContext);
  const open = accordion?.type === "multiple"
    ? Array.isArray(accordion.value) && accordion.value.includes(itemValue)
    : accordion?.value === itemValue;

  if (!open) return null;

  return (
    <div
      ref={ref}
      className={cn("tw-overflow-hidden tw-pb-4 tw-text-sm tw-text-muted", className)}
      data-state="open"
      {...props}
    >
      {children}
    </div>
  );
});
AccordionContent.displayName = "AccordionContent";

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
